// src/services/authService.ts

import api from './axiosInstance';

const TOKEN_KEY = "accessToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const ROLE_KEY = "userRole";
const PERMISSIONS_KEY = "permissions";
const USER_KEY = "user";
const ACTIVE_DEPARTMENT_KEY = "activeDepartment";
const AVAILABLE_DEPARTMENTS_KEY = "availableDepartments";
const ROLES_BY_DEPARTMENT_KEY = "rolesByDepartment";

interface TokenPayload {
  exp: number;
  iat: number;
  sub: string;
  role?: string;
  [key: string]: any;
}

// API Response Types
interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  errors?: Array<{
    field: string;
    message: string;
  }>;
  developmentInfo?: any;
}

interface LoginResponse {
  user: any;
  tokens: {
    access: {
      token: string;
      expires: string;
    };
    refresh: {
      token: string;
      expires: string;
    };
  };
}

interface RegisterRequest {
  name: string;
  email?: string;
  mobile?: string;
  password: string;
  role: string;
}

interface LoginRequest {
  email?: string;
  mobile?: string;
  password: string;
}

// Interfaces kept for future use
// interface ForgotPasswordRequest {
//   email: string;
// }

// interface ResetPasswordRequest {
//   token: string;
//   password: string;
// }

// interface SetPasswordRequest {
//   token: string;
//   password: string;
// }

export const authService = {
  /**
   * Login user with backend API
   */
  loginUser: async (credentials: LoginRequest): Promise<any> => {
    try {
      const response = await api.post<ApiResponse<LoginResponse>>('/auth/login', credentials);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Login failed');
      }

      const { user, tokens } = response.data.data;

      // Store authentication data
      authService.login(
        tokens.access.token,
        tokens.refresh.token,
        user.role?.name || '',
        user.role?.permissions || {},
        user,
        user.activeDepartment || 'OMS',
        user.roles ? user.roles.map((r: any) => r.department) : ['OMS'],
        user.roles ? user.roles.reduce((acc: any, curr: any) => ({ ...acc, [curr.department]: curr }), {}) : { OMS: user.role }
      );

      return { user, tokens };
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.message || 'Login failed');
    }
  },

  /**
   * Register new user with backend API
   */
  registerUser: async (userData: RegisterRequest): Promise<any> => {
    try {
      const response = await api.post<ApiResponse<LoginResponse>>('/auth/register', userData);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Registration failed');
      }

      const { user, tokens } = response.data.data;

      // Store authentication data
      authService.login(
        tokens.access.token,
        tokens.refresh.token,
        user.role?.name || '',
        user.role?.permissions || {},
        user,
        user.activeDepartment || 'OMS',
        user.roles ? user.roles.map((r: any) => r.department) : ['OMS'],
        user.roles ? user.roles.reduce((acc: any, curr: any) => ({ ...acc, [curr.department]: curr }), {}) : { OMS: user.role }
      );

      return { user, tokens };
    } catch (error: any) {
      console.error('Registration error:', error);
      throw new Error(error.message || 'Registration failed');
    }
  },

  /**
   * Logout user with backend API
   */
  logoutUser: async (): Promise<void> => {
    try {
      const refreshToken = authService.getRefreshToken();
      if (refreshToken) {
        await api.post('/auth/logout', { refreshToken });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      authService.logout();
    }
  },

  /**
   * Forgot password request
   */
  forgotPassword: async (email: string): Promise<void> => {
    try {
      const response = await api.post<ApiResponse<null>>('/auth/forgot-password', { email });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to send reset email');
      }
    } catch (error: any) {
      console.error('Forgot password error:', error);
      throw new Error(error.message || 'Failed to send reset email');
    }
  },

  /**
   * Reset password with token
   */
  resetPassword: async (token: string, password: string): Promise<void> => {
    try {
      const response = await api.post<ApiResponse<null>>('/auth/reset-password', {
        token,
        password
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to reset password');
      }
    } catch (error: any) {
      console.error('Reset password error:', error);
      throw new Error(error.message || 'Failed to reset password');
    }
  },

  /**
   * Set password with token (for new users)
   */
  setPassword: async (token: string, password: string): Promise<void> => {
    try {
      const response = await api.post<ApiResponse<null>>('/auth/set-password', {
        token,
        password
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to set password');
      }
    } catch (error: any) {
      console.error('Set password error:', error);
      throw new Error(error.message || 'Failed to set password');
    }
  },

  /**
   * Change password for the logged-in user.
   * Sends the stored refresh token so the backend preserves THIS session
   * while revoking every other one. Returns { success, message } instead of
   * throwing so the modal can render field-level errors.
   */
  changePassword: async (
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; message: string }> => {
    try {
      const refreshToken = authService.getRefreshToken();
      const response = await api.post<ApiResponse<null>>('/auth/change-password', {
        currentPassword,
        newPassword,
        ...(refreshToken ? { refreshToken } : {}),
      });
      return {
        success: true,
        message: response.data.message || 'Password changed successfully',
      };
    } catch (error: any) {
      console.error('Change password error:', error);
      return {
        success: false,
        message: error?.response?.data?.message || 'Failed to change password',
      };
    }
  },

  /**
   * Send email verification
   */
  sendVerificationEmail: async (): Promise<void> => {
    try {
      const response = await api.post<ApiResponse<null>>('/auth/send-verification-email');

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to send verification email');
      }
    } catch (error: any) {
      console.error('Send verification email error:', error);
      throw new Error(error.message || 'Failed to send verification email');
    }
  },

  /**
   * Verify email with token
   */
  verifyEmail: async (token: string): Promise<void> => {
    try {
      const response = await api.post<ApiResponse<null>>('/auth/verify-email', { token });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to verify email');
      }
    } catch (error: any) {
      console.error('Verify email error:', error);
      throw new Error(error.message || 'Failed to verify email');
    }
  },

  /**
   * Send OTP to mobile
   */
  sendOtp: async (mobile: string): Promise<{ success: boolean; developmentInfo?: any }> => {
    try {
      const response = await api.post<ApiResponse<any>>('/auth/send-otp', { mobile });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to send OTP');
      }

      // Check if we have development info to show
      if (response.data.developmentInfo) {
        console.log('🚧 Development Mode OTP Info:', response.data.developmentInfo);

        // Show development info in console for easy access
        if (response.data.developmentInfo.messages) {
          console.log('📱 Generated OTPs:', response.data.developmentInfo.messages);
        }
        if (response.data.developmentInfo.bypassOtp) {
          console.log('🔑 Bypass OTP:', response.data.developmentInfo.bypassOtp);
        }

        return {
          success: true,
          developmentInfo: response.data.developmentInfo
        };
      }

      return { success: true };
    } catch (error: any) {
      console.error('Send OTP error:', error);
      throw new Error(error.message || 'Failed to send OTP');
    }
  },

  /**
   * Login with OTP
   */
  loginWithOtp: async (mobile: string, otp: string): Promise<any> => {
    try {
      const response = await api.post<ApiResponse<LoginResponse>>('/auth/login-with-otp', {
        mobile,
        otp
      });

      if (!response.data.success) {
        throw new Error(response.data.message || 'OTP login failed');
      }

      const { user, tokens } = response.data.data;

      // Store authentication data
      authService.login(
        tokens.access.token,
        tokens.refresh.token,
        user.role?.name || '',
        user.role?.permissions || {},
        user,
        user.activeDepartment || 'OMS',
        user.roles ? user.roles.map((r: any) => r.department) : ['OMS'],
        user.roles ? user.roles.reduce((acc: any, curr: any) => ({ ...acc, [curr.department]: curr }), {}) : { OMS: user.role }
      );

      return { user, tokens };
    } catch (error: any) {
      console.error('OTP login error:', error);
      throw new Error(error.message || 'OTP login failed');
    }
  },

  /**
   * Switch department
   */
  switchDepartment: async (department: string): Promise<any> => {
    try {
      const response = await api.post<ApiResponse<LoginResponse>>('/auth/switch-department', { department });

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to switch department');
      }

      const { user, tokens } = response.data.data;

      // Web browsers receive the refresh token via HttpOnly cookie, so tokens.refresh
      // may be absent from the response body. Fall back to the stored refresh token.
      const refreshToken = tokens.refresh?.token ?? authService.getRefreshToken() ?? '';

      // Update auth data
      authService.login(
        tokens.access.token,
        refreshToken,
        user.role?.name || '',
        user.role?.permissions || {},
        user,
        user.activeDepartment || department,
        user.roles ? user.roles.map((r: any) => r.department) : ['OMS'],
        user.roles ? user.roles.reduce((acc: any, curr: any) => ({ ...acc, [curr.department]: curr }), {}) : { OMS: user.role }
      );

      return { user, tokens: { ...tokens, refresh: { token: refreshToken } } };
    } catch (error: any) {
      console.error('Switch department error:', error);
      throw new Error(error.message || 'Failed to switch department');
    }
  },

  /**
   * Login and store tokens
   */
  login: (
    accessToken: string, 
    refreshToken: string, 
    role: string, 
    permissions: any, 
    user: any,
    activeDepartment: string = 'OMS',
    availableDepartments: string[] = ['OMS'],
    rolesByDepartment: any = {}
  ) => {
    console.log('AuthService - Login called with:', {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      role,
      hasPermissions: !!permissions,
      hasUser: !!user,
      activeDepartment,
      availableDepartments
    });

    authService.setToken(accessToken);
    authService.setRefreshToken(refreshToken);
    authService.setRole(role);
    authService.setPermissions(permissions);
    authService.setUser(user);
    authService.setActiveDepartment(activeDepartment);
    authService.setAvailableDepartments(availableDepartments);
    authService.setRolesByDepartment(rolesByDepartment);

    console.log('AuthService - Tokens stored successfully');
  },

  /**
   * Logout and clear all stored data
   */
  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(PERMISSIONS_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(ACTIVE_DEPARTMENT_KEY);
    localStorage.removeItem(AVAILABLE_DEPARTMENTS_KEY);
    localStorage.removeItem(ROLES_BY_DEPARTMENT_KEY);
  },

  /**
   * Get access token
   */
  getToken: (): string | null => {
    const token = localStorage.getItem(TOKEN_KEY);
    console.log('AuthService - getToken called, token:', token ? 'Present' : 'Missing');
    return token;
  },

  /**
   * Set access token
   */
  setToken: (token: string) => {
    localStorage.setItem(TOKEN_KEY, token);
  },

  /**
   * Get refresh token
   */
  getRefreshToken: (): string | null => {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  /**
   * Set refresh token
   */
  setRefreshToken: (token: string) => {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  },

  /**
   * Get user role (role name string)
   */
  getRole: (): string | null => {
    return localStorage.getItem(ROLE_KEY);
  },

  /**
   * Get current role ID for feature/API context (department-wise features).
   * Use with getActiveDepartment() to determine behavior by role and department.
   */
  getRoleId: (): string | null => {
    const user = authService.getUser();
    if (!user) return null;
    if (user.roleId) return typeof user.roleId === 'object' && user.roleId?.id ? user.roleId.id : String(user.roleId);
    if (user.role?._id) return typeof user.role._id === 'object' && user.role._id?.toString ? user.role._id.toString() : String(user.role._id);
    return null;
  },

  /**
   * Set user role
   */
  setRole: (role: string) => {
    localStorage.setItem(ROLE_KEY, role);
  },

  /**
   * Get user permissions
   */
  getPermissions: (): any => {
    const permissions = localStorage.getItem(PERMISSIONS_KEY);
    return permissions ? JSON.parse(permissions) : null;
  },

  /**
   * Set user permissions
   */
  setPermissions: (permissions: any) => {
    localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(permissions));
  },

  /**
   * Get active department
   */
  getActiveDepartment: (): string => {
    return localStorage.getItem(ACTIVE_DEPARTMENT_KEY) || 'OMS';
  },

  /**
   * Set active department
   */
  setActiveDepartment: (department: string) => {
    localStorage.setItem(ACTIVE_DEPARTMENT_KEY, department);
  },

  /**
   * Get available departments
   */
  getAvailableDepartments: (): string[] => {
    const depts = localStorage.getItem(AVAILABLE_DEPARTMENTS_KEY);
    return depts ? JSON.parse(depts) : ['OMS'];
  },

  /**
   * Set available departments
   */
  setAvailableDepartments: (departments: string[]) => {
    localStorage.setItem(AVAILABLE_DEPARTMENTS_KEY, JSON.stringify(departments));
  },

  /**
   * Get roles by department
   */
  getRolesByDepartment: (): any => {
    const roles = localStorage.getItem(ROLES_BY_DEPARTMENT_KEY);
    return roles ? JSON.parse(roles) : {};
  },

  /**
   * Set roles by department
   */
  setRolesByDepartment: (roles: any) => {
    localStorage.setItem(ROLES_BY_DEPARTMENT_KEY, JSON.stringify(roles));
  },

  /**
   * Get user data
   */
  getUser: (): any => {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  },

  /**
   * Set user data
   */
  setUser: (user: any) => {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  },

  /**
   * Update user data (merge with existing data)
   */
  updateUser: (userData: any) => {
    const currentUser = authService.getUser();
    if (currentUser) {
      const updatedUser = { ...currentUser, ...userData };
      authService.setUser(updatedUser);
    }
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: (): boolean => {
    const token = authService.getToken();
    return !!token && !authService.isTokenExpired();
  },

  /**
   * Check if token is expired
   */
  isTokenExpired: (): boolean => {
    const token = authService.getToken();
    if (!token) {
      console.log('AuthService - isTokenExpired: No token found');
      return true;
    }

    try {
      const payload = authService.decodeToken(token);
      const currentTime = Math.floor(Date.now() / 1000);
      const isExpired = payload.exp < currentTime;
      console.log('AuthService - isTokenExpired:', {
        tokenExp: payload.exp,
        currentTime,
        isExpired
      });
      return isExpired;
    } catch (error) {
      console.error('AuthService - Error decoding token:', error);
      return true;
    }
  },

  /**
   * Check if token will expire soon (within 5 minutes)
   */
  isTokenExpiringSoon: (): boolean => {
    const token = authService.getToken();
    if (!token) return true;

    try {
      const payload = authService.decodeToken(token);
      const currentTime = Math.floor(Date.now() / 1000);
      const fiveMinutes = 5 * 60; // 5 minutes in seconds
      return payload.exp < (currentTime + fiveMinutes);
    } catch (error) {
      console.error('Error decoding token:', error);
      return true;
    }
  },

  /**
   * Decode JWT token
   */
  decodeToken: (token: string): TokenPayload => {
    try {
      console.log('AuthService - decodeToken called with token length:', token.length);

      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      const payload = JSON.parse(jsonPayload);
      console.log('AuthService - decodeToken successful, payload:', {
        sub: payload.sub,
        type: payload.type,
        exp: payload.exp,
        iat: payload.iat
      });

      return payload;
    } catch (error) {
      console.error('AuthService - decodeToken error:', error);
      throw new Error('Invalid token format');
    }
  },

  /**
   * Get token payload
   */
  getTokenPayload: (): TokenPayload | null => {
    const token = authService.getToken();
    if (!token) return null;

    try {
      return authService.decodeToken(token);
    } catch (error) {
      return null;
    }
  },

  /**
   * Refresh access token using refresh token
   */
  refreshToken: async (): Promise<boolean> => {
    const refreshToken = authService.getRefreshToken();
    if (!refreshToken) {
      console.error('No refresh token available');
      return false;
    }

    try {
      const response = await api.post<ApiResponse<{ tokens: LoginResponse['tokens'] }>>('/auth/refresh-tokens', {
        refreshToken
      });

      if (response.data.success) {
        const { tokens } = response.data.data;
        authService.setToken(tokens.access.token);
        if (tokens.refresh) {
          authService.setRefreshToken(tokens.refresh.token);
        }
        return true;
      } else {
        console.error('Token refresh failed');
        return false;
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw error;
    }
  },

  /**
   * Get all stored auth data (includes roleId and activeDepartment for feature/department context)
   */
  getAuthData: () => {
    return {
      token: authService.getToken(),
      refreshToken: authService.getRefreshToken(),
      role: authService.getRole(),
      roleId: authService.getRoleId(),
      permissions: authService.getPermissions(),
      user: authService.getUser(),
      activeDepartment: authService.getActiveDepartment(),
      availableDepartments: authService.getAvailableDepartments(),
      rolesByDepartment: authService.getRolesByDepartment(),
    };
  },

  /**
   * Set all auth data at once
   */
  setAuthData: (data: {
    token: string;
    refreshToken: string;
    role: string;
    permissions: any;
    user: any;
    activeDepartment?: string;
    availableDepartments?: string[];
    rolesByDepartment?: any;
  }) => {
    authService.setToken(data.token);
    authService.setRefreshToken(data.refreshToken);
    authService.setRole(data.role);
    authService.setPermissions(data.permissions);
    authService.setUser(data.user);
    if (data.activeDepartment) authService.setActiveDepartment(data.activeDepartment);
    if (data.availableDepartments) authService.setAvailableDepartments(data.availableDepartments);
    if (data.rolesByDepartment) authService.setRolesByDepartment(data.rolesByDepartment);
  },

  /**
   * Clear all auth data
   */
  clearAuth: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(ROLE_KEY);
    localStorage.removeItem(PERMISSIONS_KEY);
    localStorage.removeItem(USER_KEY);
  },

  /**
   * Check if refresh token is available
   */
  hasRefreshToken: (): boolean => {
    return !!authService.getRefreshToken();
  },

  /**
   * Get token expiration time
   */
  getTokenExpiration: (): Date | null => {
    const payload = authService.getTokenPayload();
    return payload ? new Date(payload.exp * 1000) : null;
  },

  /**
   * Get time until token expires (in seconds)
   */
  getTimeUntilExpiration: (): number => {
    const payload = authService.getTokenPayload();
    if (!payload) return 0;

    const currentTime = Math.floor(Date.now() / 1000);
    return Math.max(0, payload.exp - currentTime);
  },
};
